import { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, Wallet, Users, Inbox, CheckCircle2 } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

export default function Transparency() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => {
    try { const { data } = await api.get('/transparency'); setData(data); }
    finally { setLoading(false); }
  })(); }, []);

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-24 text-muted-foreground">{t.common.loading}</div>;

  const { aggregates, finance, changelog } = data;
  const stats = [
    { key: 'donations', icon: TrendingUp, label: t.transparency.donations, value: `€${aggregates.donations_total.toLocaleString()}`, color: '#10B981' },
    { key: 'expenses', icon: TrendingDown, label: t.transparency.expenses, value: `€${aggregates.expenses_total.toLocaleString()}`, color: '#F59E0B' },
    { key: 'balance', icon: Wallet, label: t.transparency.balance, value: `€${aggregates.balance.toLocaleString()}`, color: '#C5A059' },
    { key: 'users', icon: Users, label: t.transparency.users, value: aggregates.users_count, color: '#0EA5E9' },
    { key: 'requests', icon: Inbox, label: t.transparency.requests, value: aggregates.requests_count, color: '#8B5CF6' },
    { key: 'resolved', icon: CheckCircle2, label: t.transparency.resolved, value: aggregates.resolved_count, color: '#10B981' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="transparency-page">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4">Tikkun</p>
      <h1 className="font-display text-5xl md:text-6xl font-medium tracking-tight leading-tight">{t.transparency.title}</h1>
      <p className="mt-4 text-lg text-muted-foreground max-w-2xl">{t.transparency.subtitle}</p>

      <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-4">
        {stats.map((s) => (
          <div key={s.key} data-testid={`stat-${s.key}`} className="p-6 rounded-3xl border border-border bg-card">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-full mb-4" style={{ backgroundColor: `${s.color}18`, color: s.color }}>
              <s.icon size={18} strokeWidth={1.75} />
            </div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">{s.label}</div>
            <div className="font-display text-2xl md:text-3xl font-medium tracking-tight mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-20 grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight mb-6">{t.transparency.ledger}</h2>
          <div className="rounded-3xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-muted-foreground">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-xs uppercase tracking-widest">{t.transparency.date}</th>
                  <th className="text-left py-3 px-4 font-medium text-xs uppercase tracking-widest">{t.transparency.type}</th>
                  <th className="text-left py-3 px-4 font-medium text-xs uppercase tracking-widest">{t.transparency.label}</th>
                  <th className="text-right py-3 px-4 font-medium text-xs uppercase tracking-widest">{t.transparency.amount}</th>
                </tr>
              </thead>
              <tbody>
                {finance.map((f) => (
                  <tr key={f.id} className="border-t border-border" data-testid={`finance-row-${f.id}`}>
                    <td className="py-3 px-4 text-muted-foreground">{f.date}</td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${f.type === 'donation' ? 'text-emerald-600 bg-emerald-500/10' : 'text-amber-600 bg-amber-500/10'}`}>
                        {f.type === 'donation' ? t.transparency.donation : t.transparency.expense}
                      </span>
                    </td>
                    <td className="py-3 px-4">{f.label}</td>
                    <td className={`py-3 px-4 text-right font-medium ${f.type === 'donation' ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {f.type === 'donation' ? '+' : '-'}€{f.amount.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight mb-6">{t.transparency.changelog}</h2>
          <ol className="space-y-4">
            {changelog.map((c) => (
              <li key={c.id} data-testid={`changelog-${c.id}`} className="p-5 rounded-2xl border border-border bg-card">
                <div className="text-xs text-muted-foreground mb-1">{c.date}</div>
                <div className="font-medium">{c.title}</div>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{c.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
